import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Settings, 
  Save,
  RefreshCw,
  Key,
  Languages,
  FileText,
  Scan
} from 'lucide-react';
import { toast } from 'sonner';
import { ApiKeysCard } from '../components/ApiKeysCard';

const DEFAULT_SETTINGS = {
  default_language: 'en',
  default_parser: 'pdfjs',
  auto_parse_on_upload: false,
  auto_validate_after_parse: false,
  openai_api_key: '',
  google_docai_key: '',
  azure_endpoint: '',
  azure_key: '',
  custom_metadata_fields: '',
};

export default function SettingsPage() {
  const { api, user } = useAuth();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api().get('/settings');
      setSettings({ ...DEFAULT_SETTINGS, ...response.data });
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api().put('/settings', settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">Settings</h1>
          <p className="text-muted-foreground mt-1">Configure your preferences and API keys</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Account Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Account
          </CardTitle>
          <CardDescription>Your account information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground text-sm">Name</Label>
              <p className="font-medium">{user?.name}</p>
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">Email</Label>
              <p className="font-medium">{user?.email}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Default Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Document Defaults
          </CardTitle>
          <CardDescription>Default settings for new documents</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Languages className="w-4 h-4" />
                Default Language
              </Label>
              <Select 
                value={settings.default_language} 
                onValueChange={(v) => updateSetting('default_language', v)}
              >
                <SelectTrigger data-testid="default-language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="es">Spanish</SelectItem>
                  <SelectItem value="fr">French</SelectItem>
                  <SelectItem value="de">German</SelectItem>
                  <SelectItem value="ar">Arabic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Scan className="w-4 h-4" />
                Default Parser
              </Label>
              <Select 
                value={settings.default_parser} 
                onValueChange={(v) => updateSetting('default_parser', v)}
              >
                <SelectTrigger data-testid="default-parser-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tesseract">Tesseract OCR</SelectItem>
                  <SelectItem value="pdfjs">PDF.js</SelectItem>
                  <SelectItem value="openai_vision">OpenAI Vision</SelectItem>
                  <SelectItem value="google_docai">Google Document AI</SelectItem>
                  <SelectItem value="azure_docint">Azure Doc Intelligence</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-parse on upload</Label>
                <p className="text-sm text-muted-foreground">Automatically parse documents when uploaded</p>
              </div>
              <Switch
                checked={settings.auto_parse_on_upload}
                onCheckedChange={(v) => updateSetting('auto_parse_on_upload', v)}
                data-testid="auto-parse-switch"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-validate after parse</Label>
                <p className="text-sm text-muted-foreground">Run validation automatically after parsing</p>
              </div>
              <Switch
                checked={settings.auto_validate_after_parse}
                onCheckedChange={(v) => updateSetting('auto_validate_after_parse', v)}
                data-testid="auto-validate-switch"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* API Keys */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
            <Key className="w-5 h-5" />
            API Keys
          </CardTitle>
          <CardDescription>Configure external service credentials</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>OpenAI API Key (for Vision)</Label>
            <Input
              type="password"
              placeholder="sk-..."
              value={settings.openai_api_key}
              onChange={(e) => updateSetting('openai_api_key', e.target.value)}
              data-testid="openai-key-input"
            />
            <p className="text-xs text-muted-foreground">Optional: Uses Emergent Key if not provided</p>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label>Google Document AI Key</Label>
            <Input
              type="password"
              placeholder="Paste your service account JSON key"
              value={settings.google_docai_key}
              onChange={(e) => updateSetting('google_docai_key', e.target.value)}
              data-testid="google-key-input"
            />
          </div>

          <Separator />

          <div className="space-y-4">
            <Label>Azure Document Intelligence</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Endpoint URL</Label>
                <Input
                  placeholder="https://..."
                  value={settings.azure_endpoint}
                  onChange={(e) => updateSetting('azure_endpoint', e.target.value)}
                  data-testid="azure-endpoint-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">API Key</Label>
                <Input
                  type="password"
                  placeholder="Your Azure key"
                  value={settings.azure_key}
                  onChange={(e) => updateSetting('azure_key', e.target.value)}
                  data-testid="azure-key-input"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Custom Metadata */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-['Manrope']">Custom Metadata Fields</CardTitle>
          <CardDescription>Define additional metadata fields for documents</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label>Field Names (comma-separated)</Label>
            <Input
              placeholder="author, department, version, category"
              value={settings.custom_metadata_fields}
              onChange={(e) => updateSetting('custom_metadata_fields', e.target.value)}
              data-testid="metadata-fields-input"
            />
            <p className="text-xs text-muted-foreground">
              These fields will be available when uploading documents
            </p>
          </div>
        </CardContent>
      </Card>

      {/* API Access */}
      <ApiKeysCard />
    </div>
  );
}
